import React, { useState, useEffect, useRef } from "react";
import "./App.css";

function App() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [botName, setBotName] = useState("Askra");
  const [logo, setLogo] = useState("/logo.png");
  const chatEndRef = useRef(null);

  // =========================
  // Sanitize Colors
  // =========================
  const sanitizeColor = (color, fallback) => {
   const hex = /^#[0-9A-Fa-f]{6}$/.test(color);
   return hex ? color : fallback;
};

  // =========================
  // Detect companyId
  // =========================
  const params = new URLSearchParams(window.location.search);
  const companyId = params.get("companyId") || "default";


  // =========================
  // Detect widget mode
  // =========================
  let isWidget = false;
  try {
    isWidget = window.self !== window.top;
  } catch {
    isWidget = true;
  }

  // =========================
  // Persistent userId
  // =========================
  let userId = localStorage.getItem("jetUserId");
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem("jetUserId", userId);
  }

  // =========================
  // LOAD COMPANY SETTINGS
  // =========================
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-settings?companyId=${companyId}`
        );

        if (!res.ok) return;

        const data = await res.json();

        // Apply CSS variables dynamically
        document.documentElement.style.setProperty("--primary-color", sanitizeColor(data.primaryColor, "#000000"));
        document.documentElement.style.setProperty("--secondary-color", sanitizeColor(data.secondaryColor, "#7c3aed"));
        document.documentElement.style.setProperty("--accent-color", sanitizeColor(data.accentColor, "#52188B"));
        document.documentElement.style.setProperty("--text-color", sanitizeColor(data.textColor, "#ffffff"));
        document.documentElement.style.setProperty("--bot-bubble-color", sanitizeColor(data.botBubbleColor, "#2a2a2a"));
        document.documentElement.style.setProperty("--glow-color", sanitizeColor(data.secondaryColor, "#6366f1"));

        if (data.botName) setBotName(data.botName);
        if (data.logoUrl) setLogo(data.logoUrl);



      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    };

    loadSettings();
  }, [companyId]);

  // =========================
  // LOAD CHAT HISTORY
  // =========================
  useEffect(() => {
  const loadHistory = async () => {
    try {
      const settingsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-settings?companyId=${companyId}`
      );
      const settingsData = settingsRes.ok ? await settingsRes.json() : {};

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/history?userId=${userId}&companyId=${companyId}`
      );

      if (!res.ok) return;

      const data = await res.json();

      if (settingsData.openingMessage && data.length === 0) {
        setMessages([{ type: "bot", text: settingsData.openingMessage, timestamp: new Date() }]);
      } else {
        setMessages(data);
      }
    } catch (err) {
      console.error("Failed to load history");
    }
  };

    loadHistory();
  }, [companyId, userId]);

  // =========================
  // Scroll to bottom
  // =========================
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  // =========================
  // Send Message
  // =========================
  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = {
      type: "user",
      text: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setTyping(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/chat`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: input,
            userId,
            companyId
          })
        }
      );

      const data = await res.json();

      const botMessage = {
        type: "bot",
        text: data.reply,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

    } catch {
      setMessages(prev => [
        ...prev,
        {
          type: "bot",
          text: "Askra is having trouble right now.",
          timestamp: new Date()
        }
      ]);
    }

    setTyping(false);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") sendMessage();
  };

  const formatTime = date => {
    const h = date.getHours();
    const m = date.getMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${ampm}`;
  };

  return (
    <div className={isWidget ? "widget-mode" : "neon-wrapper"}>
      <div className="chat-container">
        <div className="chat-header">
          <img src={logo} alt="Bot Logo" className="chat-logo" />
          <span className="chat-title">{botName}</span>
        </div>

        <div id="chat">
          {messages.map((msg, i) => (
            <div key={i} className={`message ${msg.type}`}>
              {msg.text}
              <div className="timestamp">
                {formatTime(new Date(msg.timestamp))}
              </div>
            </div>
          ))}

          {typing && (
            <div className="message bot">
              <div className="typing">
                <span>{botName} is typing</span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        <div className="input-container">
          <input
            type="text"
            placeholder={`Ask ${botName}...`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button onClick={sendMessage}>Send</button>
        </div>
      </div>
    </div>
  );
}


export default App;